import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Controller} from 'react-hook-form';
import get from 'lodash/get';
import {alpha, makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Button from '@material-ui/core/Button';
import {Add, DeleteOutlined, EditOutlined} from '@material-ui/icons';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import GenericTab from '../../../shared/generictab/GenericTab.js';
import FileSelector from '../../../shared/fileselector/FileSelector.js';
import BlocoErro from '../../../shared/listaerros/BlocoErro.js';
import Message from '../../../shared/message/Message.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import DialogEscolaridade from '../../../publica/Page/inscricao/DialogEscolaridade.jsx';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import useErrorMessage from '../../../../hooks/useErrorMessage.js';
import {arquivoParaBase64} from '../../../../utils/arquivo.js';
import {parseDecimal} from '../../../../utils/index.js';
import {buttonTableCell, containedButton, tituloSecao} from '../../../../utils/styles.js';
import {CATEGORIAS, dataBrValida} from '../../components/inscricao/inscricaoAdministrativaPayload.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO} from '../../components/paginacao.js';
import {indiceSecaoVisivel, PONTUACAO_ENEM} from '../../components/inscricao/inscricaoAdministrativaSchema.js';

/* Mesmas larguras e títulos da etapa Dados da Inscrição da inscrição pública. */
const useStyles = makeStyles(theme => ({
    tabs: {
        width: '100%',
        minWidth: '180px',
        maxWidth: '220px',
        marginRight: theme.spacing(2),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '180px',
                maxWidth: '220px',
                alignItems: 'flex-start',
                textAlign: 'left'
            }
        }
    },
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    // Rótulo no topo e nota embaixo: as notas do INEP ficam na mesma linha mesmo com rótulo em duas linhas.
    notaInep: {
        height: '100%',
        justifyContent: 'space-between'
    },
    mediaDivergente: {
        backgroundColor: alpha(theme.palette.error.main, 0.15),
        borderRadius: theme.shape.borderRadius,
        // Só na horizontal: na vertical tiraria a média do alinhamento com as outras notas.
        padding: theme.spacing(0, 1),
        margin: theme.spacing(0, -1)
    },
    ...buttonTableCell(theme),
    ...containedButton(theme, false, false)
}));

const SIM_NAO = [{valor: 'true', descricao: 'Sim'}, {valor: 'false', descricao: 'Não'}];

const TIPOS_ENSINO = {
    PU: 'Pública',
    PI: 'Privada com bolsa integral',
    PP: 'Privada com bolsa parcial',
    PS: 'Privada sem bolsa',
    P2: 'Pública / Privada'
};

/* Nota do INEP com 2 casas decimais (pt-BR); "-" sem valor. */
const formatarNota = nota => (nota === null || nota === undefined
    ? ouTraco(nota)
    : Number(nota).toLocaleString('pt-BR', {minimumFractionDigits: 2, maximumFractionDigits: 2}));

const FORMATOS_COMPROVANTE_ENEM = [
    {format: 'PDF', mimeType: ['application/pdf', 'application/x-download', 'application/octet-stream']},
    {format: 'GIF', mimeType: ['image/gif']},
    {format: 'JPG', mimeType: ['image/jpeg', 'image/pjpeg']},
    {format: 'PNG', mimeType: ['image/png']}
];

// O comprovante vai em base64 dentro do JSON da inscrição: o limite é o mesmo do fluxo público (5 MB).
const TAMANHO_MAXIMO_COMPROVANTE = 5 * 1024 * 1024;

/*
 * Etapa "Dados da Inscrição": Opções de Inscrição (vaga e cotas), Processo e
 * Categoria, Dados de Veterano, Escolaridade, Pontuações e Motivação —
 * cada seção só nos programas em que o _form.xhtml do PrimeFaces a exibe.
 */
export default function EtapaDadosInscricao(props) {
    const {
        control,
        errors,
        watch,
        setValue,
        secoesComErro,
        secaoFoco,
        regras,
        vagas = [],
        psInscricoes = [],
        enems = [],
        anoEnem = null,
        anoInep = null,
        onAlterarAnoInep,
        resultadoEnem = null,
        onSolicitarInep,
        pontuacaoProcessoSeletivo = null,
        redacaoProcessoSeletivo = null,
        formacoes = [],
        dataAtualizacaoPSInscricao
    } = props;
    const classes = useStyles();
    const tabRef = useRef();
    const confirmation = useConfirmationDialog();
    const showErrorMessage = useErrorMessage();
    // Índice da escolaridade em edição: -1 para adicionar, null com o diálogo fechado.
    const [indiceEscolaridade, setIndiceEscolaridade] = useState(null);
    const anosEnem = [...new Set(enems.map(item => item.enem?.anoEnem).filter(Boolean))];

    const utilizarEnem = watch('utilizarEnem') === 'true';
    const nomeArquivoEnem = watch('inscricaoEnem.nomeArquivo');
    const possuiBeneficio = watch('inscricaoVeterano.possuiBeneficioNaIes') === 'true';
    const participouAnterior = watch('inscricaoVeterano.participouProcessoAnterior') === 'true';
    const escolaridades = watch('inscricaoEnsino') || [];
    // Média informada x média do INEP, comparadas com 2 casas decimais (só com a pontuação em formato válido).
    const pontuacaoInformada = String(watch('inscricaoEnem.pontuacao') ?? '').trim();
    const mediaInformada = PONTUACAO_ENEM.test(pontuacaoInformada) ? parseDecimal(pontuacaoInformada) : null;
    const mediaInep = resultadoEnem?.media ?? null;
    const mediaDivergente = utilizarEnem && mediaInformada !== null && mediaInep !== null
        && Number(resultadoEnem?.numAno) === Number(anoEnem)
        && Math.round(mediaInformada * 100) !== Math.round(Number(mediaInep) * 100);

    const erro = nome => get(errors, nome);
    const mensagemErro = nome => !!erro(nome) && <FormHelperText error>{erro(nome).message}</FormHelperText>;

    const titulo = texto => (
        <Grid item xs={12}>
            <h4 className={classes.sectionTitle}>{texto}</h4>
        </Grid>
    );

    // Título do resultado do INEP com o filtro de ano e o botão de solicitação à direita.
    const tituloInep = texto => (
        <Grid item xs={12}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <h4 className={classes.sectionTitle}>{texto}</h4>
                <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
                    <Select
                        value={anoInep ? String(anoInep) : ''}
                        displayEmpty
                        style={{minWidth: 100}}
                        onChange={event => onAlterarAnoInep(Number(event.target.value))}
                        inputProps={{'aria-label': 'Ano do resultado do INEP'}}>
                        <MenuItem value='' disabled>Ano</MenuItem>
                        {anosEnem.map(item => <MenuItem key={item} value={String(item)}>{item}</MenuItem>)}
                    </Select>
                    {onSolicitarInep && (
                        <Button
                            variant='contained'
                            className={classes.containedButton}
                            disabled={!anoInep}
                            onClick={() => onSolicitarInep(Number(anoInep))}>
                            Solicitar ao INEP
                        </Button>
                    )}
                </div>
            </div>
        </Grid>
    );

    const campoTexto = (nome, rotulo, {disabled = false, multiline = false, sm = 6, maxLength, placeholder} = {}) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro(nome)} disabled={disabled}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <OutlinedInput
                                name={field.name}
                                value={field.value ?? ''}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                inputRef={field.ref}
                                multiline={multiline}
                                minRows={multiline ? 5 : undefined}
                                placeholder={placeholder}
                                inputProps={maxLength ? {maxLength} : undefined}/>
                        )}/>
                    {mensagemErro(nome)}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const campoSelect = (nome, rotulo, opcoes, {disabled = false, sm = 6} = {}) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl variant='outlined' fullWidth error={!!erro(nome)} disabled={disabled}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <Select {...field} value={field.value ?? ''} displayEmpty>
                                <MenuItem value=''><em>Selecione...</em></MenuItem>
                                {opcoes.map(opcao => (
                                    <MenuItem key={opcao.valor} value={opcao.valor} title={opcao.descricao}>{opcao.descricao}</MenuItem>
                                ))}
                            </Select>
                        )}/>
                    {mensagemErro(nome)}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const campoRadio = (nome, rotulo, {sm = 6} = {}) => (
        <Grid item xs={12} sm={sm}>
            <LabelValue label={rotulo}>
                <FormControl component='fieldset' error={!!erro(nome)}>
                    <Controller
                        name={nome}
                        control={control}
                        render={({field}) => (
                            <RadioGroup row name={field.name} value={field.value ?? ''} onChange={evento => field.onChange(evento.target.value)}>
                                {SIM_NAO.map(opcao => (
                                    <FormControlLabel key={opcao.valor} value={opcao.valor} control={<Radio color='primary'/>} label={opcao.descricao}/>
                                ))}
                            </RadioGroup>
                        )}/>
                    {mensagemErro(nome)}
                </FormControl>
            </LabelValue>
        </Grid>
    );

    const selecionarComprovante = async arquivo => {
        try {
            const conteudo = await arquivoParaBase64(arquivo);
            const opcoes = {shouldDirty: true};
            setValue('inscricaoEnem.arquivo', conteudo, opcoes);
            setValue('inscricaoEnem.nomeArquivo', arquivo.name, opcoes);
            setValue('inscricaoEnem.mimetype', arquivo.type && arquivo.type !== 'application/octet-stream' ? arquivo.type : 'application/pdf', opcoes);
        } catch (e) {
            showErrorMessage('Não foi possível ler o arquivo selecionado. Tente escolhê-lo novamente.');
        }
    };

    const removerComprovante = () => {
        const opcoes = {shouldDirty: true};
        setValue('inscricaoEnem.arquivo', null, opcoes);
        setValue('inscricaoEnem.nomeArquivo', '', opcoes);
        setValue('inscricaoEnem.mimetype', '', opcoes);
    };

    /* Escolaridade: a data de conclusão só é exigida (e conferida) quando o curso está completo. */
    const salvarEscolaridade = form => {
        if (form.completo === 'S' && !dataBrValida(form.dataConclusao)) {
            showErrorMessage('Informe uma data de conclusão válida (dd/mm/aaaa).');
            return;
        }
        const atual = [...escolaridades];
        const existente = indiceEscolaridade > -1 ? atual[indiceEscolaridade] : null;
        const item = {...(existente || {}), ...form, dataConclusao: form.completo === 'S' ? form.dataConclusao : null};
        if (existente) {
            atual[indiceEscolaridade] = item;
        } else {
            atual.push(item);
        }
        setValue('inscricaoEnsino', atual, {shouldDirty: true});
        setIndiceEscolaridade(null);
    };

    const removerEscolaridade = indice => {
        confirmation('Tem certeza que deseja remover a escolaridade?', () =>
            setValue('inscricaoEnsino', escolaridades.filter((item, posicao) => posicao !== indice), {shouldDirty: true}));
    };

    const secoes = [
        {
            id: 'opcoes',
            label: 'Opções de Inscrição',
            visible: regras.exibirVaga || regras.exibirCotas,
            content: (
                <Grid container spacing={3}>
                    {titulo('Opções de Inscrição')}
                    {regras.exibirVaga && campoSelect('vaga', 'Curso de Interesse *', vagas)}
                    {regras.exibirCotas && campoRadio('sistemaCotas', 'Participa do sistema de cotas?', {sm: 5})}
                </Grid>
            )
        },
        {
            id: 'processoCategoria',
            label: 'Processo e Categoria',
            visible: regras.exibirPsInscricao || regras.exibirCategoria || !!dataAtualizacaoPSInscricao,
            content: (
                <Grid container spacing={3}>
                    {titulo('Processo e Categoria')}
                    {regras.exibirPsInscricao && campoSelect('psInscricao', 'Inscrição processo seletivo', psInscricoes)}
                    {dataAtualizacaoPSInscricao && (
                        <Grid item xs={12} sm={6}>
                            <LabelValue label='Data de atualização' value={dataAtualizacaoPSInscricao}/>
                        </Grid>
                    )}
                    {regras.exibirCategoria && campoSelect('categoria', 'Categoria *', CATEGORIAS.map(categoria => ({valor: categoria, descricao: categoria})))}
                </Grid>
            )
        },
        {
            id: 'veterano',
            label: 'Dados de Veterano',
            visible: regras.exibirVeterano,
            content: (
                <Grid container spacing={3}>
                    {titulo('Dados de Veterano')}
                    {campoRadio('inscricaoVeterano.possuiEnem', 'Possui ENEM', {sm: 12})}
                    {campoRadio('inscricaoVeterano.possuiBeneficioNaIes', 'Possui benefício na Instituição de Ensino', {sm: 12})}
                    {campoTexto('inscricaoVeterano.descricaoBeneficio', 'Descrição do benefício', {disabled: !possuiBeneficio, sm: 12, maxLength: 255})}
                    {campoRadio('inscricaoVeterano.participouProcessoAnterior', 'Participou de algum processo de seleção anterior', {sm: 12})}
                    {campoTexto('inscricaoVeterano.descricaoProcessoAnterior', 'Descrição do processo anterior', {disabled: !participouAnterior, sm: 12, maxLength: 255})}
                </Grid>
            )
        },
        {
            id: 'escolaridade',
            label: 'Escolaridade',
            content: (
                <BlocoErro erro={!!erro('inscricaoEnsino')}>
                    <Grid container spacing={3}>
                        {titulo('Escolaridade')}
                        <Grid item xs={12}>
                            <DataGrid
                                toolbarContent={(
                                    <Button variant='contained' className={classes.containedButton} startIcon={<Add/>}
                                            onClick={() => setIndiceEscolaridade(-1)}>
                                        Adicionar
                                    </Button>
                                )}
                                tableContainerProps={{component: 'div'}}
                                rows={escolaridades}
                                paginate
                                paginateOptions={PAGINACAO_PADRAO}
                                noRecordsText='Nenhum grau de ensino cadastrado.'
                                columns={[
                                    {label: 'Formação', field: 'formacao.descricao', valueGetter: (valor, row) => ouTraco(row.formacao?.descricao)},
                                    {label: 'Completo', field: 'completo', valueGetter: valor => (valor === 'S' ? 'Sim' : 'Não')},
                                    {label: 'Instituição', field: 'instituicao', valueGetter: valor => ouTraco(valor)},
                                    {label: 'Tipo de Ensino', field: 'ensinoPublico', valueGetter: valor => ouTraco(TIPOS_ENSINO[valor])},
                                    criarColunaOpcoes(row => {
                                        const indice = escolaridades.indexOf(row);
                                        return [
                                            {titulo: 'Editar', icone: <EditOutlined/>, onClick: () => setIndiceEscolaridade(indice)},
                                            {titulo: 'Remover', icone: <DeleteOutlined/>, onClick: () => removerEscolaridade(indice)}
                                        ];
                                    }, {className: classes.buttonTableCell})
                                ]}/>
                        </Grid>
                    </Grid>
                </BlocoErro>
            )
        },
        {
            id: 'enem',
            label: 'Pontuações',
            visible: regras.exibirEnem,
            content: (
                <Grid container spacing={3}>
                    {titulo('Pontuações Processo Seletivo')}
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Pontuação do Processo Seletivo' value={formatarNota(pontuacaoProcessoSeletivo)}/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <LabelValue label='Redação do Processo Seletivo' value={formatarNota(redacaoProcessoSeletivo)}/>
                    </Grid>
                    {titulo('Pontuações Enem')}
                    {campoRadio('utilizarEnem', 'Optar por utilizar a média do ENEM?', {sm: 12})}
                    {campoSelect('inscricaoEnem.processoSeletivoEnem', 'Ano do ENEM',
                        enems.map(item => ({valor: String(item.id), descricao: `ENEM ${item.enem?.anoEnem || item.id}`})),
                        {disabled: !utilizarEnem, sm: 4})}
                    {campoTexto('inscricaoEnem.cdEnem', 'Código do ENEM', {disabled: !utilizarEnem, sm: 4, maxLength: 18})}
                    {campoTexto('inscricaoEnem.pontuacao', 'Pontuação Média', {disabled: !utilizarEnem, sm: 4, maxLength: 8, placeholder: '0,0000'})}
                    <Grid item xs={12}>
                        <LabelValue label='Comprovante de Nota do ENEM (PDF, GIF, JPG ou PNG até 5 MB)'>
                            {utilizarEnem ? (
                                <FileSelector
                                    htmlFor='comprovanteEnemInscricao'
                                    buttonText='Selecionar'
                                    validFormats={FORMATOS_COMPROVANTE_ENEM}
                                    maxSize={TAMANHO_MAXIMO_COMPROVANTE}
                                    onFile={nomeArquivoEnem ? {nome: nomeArquivoEnem} : null}
                                    onSelect={selecionarComprovante}
                                    onRemove={removerComprovante}/>
                            ) : (
                                <FormControl variant='outlined' fullWidth disabled>
                                    <OutlinedInput value={nomeArquivoEnem || ''} placeholder='Nenhum comprovante de nota do ENEM enviado.'/>
                                </FormControl>
                            )}
                        </LabelValue>
                    </Grid>
                    {(anosEnem.length > 0 || resultadoEnem) && (
                        <>
                            {tituloInep(`Resultado do INEP - ${resultadoEnem?.numAno ?? anoInep ?? '-'}`)}

                            <Grid item xs={12} sm={6} md={2}>
                                <LabelValue className={classes.notaInep} label='Ciências da Natureza' value={formatarNota(resultadoEnem?.notaCn)}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <LabelValue className={classes.notaInep} label='Ciências Humanas' value={formatarNota(resultadoEnem?.notaCh)}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <LabelValue className={classes.notaInep} label='Linguagens e Códigos' value={formatarNota(resultadoEnem?.notaLc)}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <LabelValue className={classes.notaInep} label='Matemática' value={formatarNota(resultadoEnem?.notaMt)}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <LabelValue className={classes.notaInep} label='Redação' value={formatarNota(resultadoEnem?.notaRedacao)}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <LabelValue
                                    className={mediaDivergente ? `${classes.notaInep} ${classes.mediaDivergente}` : classes.notaInep}
                                    label='Média'
                                    value={formatarNota(mediaInep)}/>
                            </Grid>
                            {mediaDivergente && (
                                <Grid item xs={12}>
                                    <Message type='error' variant='standard'>
                                        {`A pontuação média informada pelo inscrito para o ENEM ${anoEnem} (${formatarNota(mediaInformada)}) é diferente da média do INEP desse ano (${formatarNota(mediaInep)}).`}
                                    </Message>
                                </Grid>
                            )}
                            {resultadoEnem?.encontrado === false && (
                                <Grid item xs={12}>
                                    <Typography variant='body2' color='textSecondary'>
                                        {['NA_FILA', 'EM_SOLICITACAO'].includes(resultadoEnem?.situacaoPedido)
                                            ? 'Pedido enviado ao INEP. O resultado ainda não está disponível.'
                                            : 'Nenhum resultado do INEP para este ano e CPF.'}
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    )}
                </Grid>
            )
        },
        {
            id: 'motivacao',
            label: 'Motivação',
            content: (
                <Grid container spacing={3}>
                    {titulo('Motivação')}
                    {campoTexto('motivo', 'Motivo da procura pelo benefício', {multiline: true, sm: 12, placeholder: 'Descreva aqui o motivo...'})}
                </Grid>
            )
        }
    ].map(secao => ({...secao, hasError: secoesComErro.has(secao.id)}));

    useEffect(() => {
        if (!secaoFoco) return;
        const indice = indiceSecaoVisivel(secoes, secaoFoco.secao);
        if (indice >= 0 && tabRef.current) {
            tabRef.current.setTabIndex(indice);
        }
    }, [secaoFoco]);

    return (
        <>
            <GenericTab
                ref={tabRef}
                orientation='vertical'
                noAppBar
                indicatorColor='primary'
                textColor='primary'
                scrollButtons='auto'
                className={classes.tabs}
                content={secoes}/>
            <DialogEscolaridade
                open={indiceEscolaridade !== null}
                onClose={() => setIndiceEscolaridade(null)}
                onSave={salvarEscolaridade}
                initialValues={indiceEscolaridade > -1 ? escolaridades[indiceEscolaridade] : null}
                formacoes={formacoes}/>
        </>
    );
}

EtapaDadosInscricao.propTypes = {
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    secoesComErro: PropTypes.instanceOf(Set).isRequired,
    secaoFoco: PropTypes.object,
    regras: PropTypes.object.isRequired,
    vagas: PropTypes.array,
    psInscricoes: PropTypes.array,
    enems: PropTypes.array,
    anoEnem: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    anoInep: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onAlterarAnoInep: PropTypes.func.isRequired,
    resultadoEnem: PropTypes.object,
    onSolicitarInep: PropTypes.func,
    pontuacaoProcessoSeletivo: PropTypes.number,
    redacaoProcessoSeletivo: PropTypes.number,
    formacoes: PropTypes.array,
    dataAtualizacaoPSInscricao: PropTypes.string
};
